import { z } from "zod";
import { createWineRequestSchema, wineTypeSchema } from "./wine.js";
/**
 * Task 10.64 — a wine somebody would like to have, before it is owned.
 *
 * **The request is the registration's request, unchanged.** That is the whole
 * of the owner's ask — *the same information I give when I register a wine* —
 * and the way to keep it true is not to write a second list of fields that
 * drifts from the first. A field added to a wine is then a field a wish can
 * carry the same day, and a wish bought is a wish whose every fact fits the
 * form it is opened in. The cost is that a wish is validated as strictly as a
 * wine: a vintage of 1750 is refused here too, which is what the owner would
 * want from a list they will later register from.
 */
export const wishRequestSchema = createWineRequestSchema;
/**
 * A page that sells a wished-for wine, as the weekly search found it — decided
 * 2026-10-17 in `project-scope.md`. The price is the shop's own figure in the
 * shop's own currency, never converted; either may be null, because a page can
 * sell a wine without showing what it costs. The address is always there, so
 * the reader can check what the model read.
 */
export const wishOfferSchema = z.object({
    shop: z.string(),
    url: z.string(),
    price: z.number().nullable(),
    currency: z.string().nullable(),
});
/**
 * A wish as the API sends it back.
 *
 * Written out rather than extended from the request, because the request's
 * fields carry `.default(...)` — right for a form that may omit a field, wrong
 * for a response, where an absent field is a broken deploy and not an
 * unanswered question. `additive.test.ts` fails on exactly that, and this is a
 * new contract with no earlier release for a default to bridge.
 *
 * **No wine number, no ratings and no provenance on the years.** A wish has no
 * number until it is bought, and a year typed into the form is stamped `Owner`
 * by the registration that follows rather than carried here.
 */
export const wishSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: wineTypeSchema,
    countryCode: z.string(),
    producer: z.string(),
    region: z.string().nullable(),
    style: z.string().nullable(),
    vintage: z.number().int().nullable(),
    grapes: z.array(z.string()),
    isNonVintage: z.boolean(),
    baseVintage: z.number().int().nullable(),
    disgorgedOn: z.string().nullable(),
    dosageGramsPerLitre: z.number().nullable(),
    alcoholPercent: z.number().nullable(),
    drinkFrom: z.number().int().nullable(),
    peakFrom: z.number().int().nullable(),
    peakTo: z.number().int().nullable(),
    drinkBy: z.number().int().nullable(),
    createdAt: z.string(),
    /**
     * When it was bought, and the wine that bought it. Both null while the wish
     * is open, and both set together: a met wish always names its wine, unless
     * that wine has since been removed, when only `metAt` is left.
     */
    metAt: z.string().nullable(),
    metByWineId: z.string().nullable(),
    /**
     * What the weekly search found, and when it last looked. Both defaulted for
     * one release, while the browser can arrive before the API that sends them:
     * no offers and *never searched* is what an older API means. Made required
     * once it is serving them.
     *
     * `searchedAt` set with no offers is *looked for and not found*; null is
     * *not yet looked for* — two facts, kept apart.
     */
    offers: z.array(wishOfferSchema).default([]),
    searchedAt: z.string().nullish(),
});
export const wishListResponseSchema = z.object({
    wishes: z.array(wishSchema),
});
/** The wine the registration made, sent once it exists. */
export const meetWishRequestSchema = z.object({
    wineId: z.string().min(1),
});
/**
 * The account's own switch for the weekly search, and whether it can be used
 * at all. `available` is false while the administrator has not allowed it and
 * for an account that owns no cellar — a guest, in the sense of the decision —
 * and the switch is then not drawn. `enabled` is the account's own choice and
 * is kept while the search is unavailable, so it resumes as it was left.
 */
export const wishSearchSettingSchema = z.object({
    available: z.boolean(),
    enabled: z.boolean(),
});
export const wishSearchRequestSchema = z.object({ enabled: z.boolean() });
/**
 * What a press of *Sök nu* did: how many wishes were searched, how many
 * failed, and whether open wishes are left for another press.
 */
export const wishSearchNowResponseSchema = z.object({
    searched: z.number().int(),
    failed: z.number().int(),
    more: z.boolean(),
});
//# sourceMappingURL=wish.js.map